/**
 * Reading the registry export of Gestify (proposal §2.7). Pure rules: from a
 * row of the file to a subject of K-Management, with what is worth telling the
 * user before importing anything.
 *
 * The export gives one row per subject, with the group (Cliente, Fornitore,
 * Cliente e Fornitore, Generico), the tax identifiers and the address. Phone
 * numbers, classifications and cost centres come empty from Gestify.
 */
import { checkTaxIds } from './tax-ids.js';
/** Columns used from the export; the others are ignored. */
export const GESTIFY_COLUMNS = {
    code: 'Codice',
    group: 'Gruppo anagrafico',
    type: 'Tipologia',
    name: 'Ragione sociale',
    address: 'Indirizzo',
    streetNumber: 'Civico',
    postalCode: 'Cap',
    city: 'Comune',
    province: 'Prov.',
    country: 'Nazione',
    vatNumber: 'Partita IVA',
    taxCode: 'Codice fiscale',
    sdiCode: 'Cod. Dest.',
    pec: 'Pec F.E.',
};
/** Country names of Gestify → ISO code. */
const COUNTRY_NAMES = {
    italia: 'IT',
    austria: 'AT',
    belgio: 'BE',
    bulgaria: 'BG',
    cina: 'CN',
    cipro: 'CY',
    croazia: 'HR',
    danimarca: 'DK',
    estonia: 'EE',
    finlandia: 'FI',
    francia: 'FR',
    germania: 'DE',
    grecia: 'GR',
    irlanda: 'IE',
    lettonia: 'LV',
    lituania: 'LT',
    lussemburgo: 'LU',
    malta: 'MT',
    norvegia: 'NO',
    'paesi bassi': 'NL',
    polonia: 'PL',
    portogallo: 'PT',
    'regno unito': 'GB',
    'repubblica ceca': 'CZ',
    romania: 'RO',
    'san marino': 'SM',
    slovacchia: 'SK',
    slovenia: 'SI',
    spagna: 'ES',
    'stati uniti d´america': 'US',
    "stati uniti d'america": 'US',
    svezia: 'SE',
    svizzera: 'CH',
    ungheria: 'HU',
};
/** Roles given by the group of Gestify; «Generico» becomes a supplier (decided with Alessandro). */
const GROUP_ROLES = {
    cliente: ['C'],
    fornitore: ['F'],
    'cliente e fornitore': ['C', 'F'],
    generico: ['F'],
};
const text = (value) => value === null || value === undefined ? '' : String(value).trim();
/** One row of the export → a subject, with the notes to show in the preview. */
export function readGestifyRow(row, line) {
    const issues = [];
    const name = text(row[GESTIFY_COLUMNS.name]);
    const countryName = text(row[GESTIFY_COLUMNS.country]).toLowerCase();
    // The export writes the name of the country, the API its ISO code («IT»).
    const isoCode = /^[a-z]{2}$/.test(countryName) ? countryName.toUpperCase() : null;
    const country = COUNTRY_NAMES[countryName] ?? isoCode ?? 'XX';
    if (countryName && !COUNTRY_NAMES[countryName] && !isoCode)
        issues.push(`Paese «${text(row[GESTIFY_COLUMNS.country])}» non riconosciuto: importato come «Altro paese»`);
    const group = text(row[GESTIFY_COLUMNS.group]).toLowerCase();
    const roles = GROUP_ROLES[group] ?? [];
    if (group === 'generico')
        issues.push('Gruppo «Generico»: importato come Fornitore');
    const rawTax = text(row[GESTIFY_COLUMNS.taxCode]);
    const personalTaxCode = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(rawTax);
    const typeCode = personalTaxCode ? 'FPF' : 'FPR';
    const ids = checkTaxIds({
        country,
        requirement: (personalTaxCode
            ? 'Codice fiscale'
            : 'P.IVA o codice fiscale'),
        vatNumber: text(row[GESTIFY_COLUMNS.vatNumber]),
        taxCode: rawTax,
        // The identifiers come from the accounting system: an unusual country format is reported, not refused.
        lenient: true,
    });
    for (const message of Object.values(ids.errors))
        issues.push(message);
    issues.push(...ids.warnings);
    const streetNumber = text(row[GESTIFY_COLUMNS.streetNumber]);
    const address = [text(row[GESTIFY_COLUMNS.address]), streetNumber].filter(Boolean).join(' ');
    const city = text(row[GESTIFY_COLUMNS.city]);
    if (!city)
        issues.push('Comune mancante');
    let sdiCode = text(row[GESTIFY_COLUMNS.sdiCode]).toUpperCase() || null;
    if (sdiCode && !/^[A-Z0-9]{6,7}$/.test(sdiCode)) {
        issues.push(`Codice destinatario «${sdiCode}» non valido: non importato`);
        sdiCode = null;
    }
    const subject = {
        typeCode,
        name,
        country,
        vatNumber: ids.vatNumber,
        taxCode: ids.taxCode,
        address: address || null,
        postalCode: text(row[GESTIFY_COLUMNS.postalCode]) || null,
        city: city || null,
        province: text(row[GESTIFY_COLUMNS.province]) || null,
        pec: text(row[GESTIFY_COLUMNS.pec]) || null,
        sdiCode,
        gestifyId: text(row[GESTIFY_COLUMNS.code]) || null,
        roles,
    };
    const error = !name
        ? 'Manca la ragione sociale'
        : roles.length === 0
            ? `Gruppo anagrafico «${text(row[GESTIFY_COLUMNS.group])}» sconosciuto`
            : !subject.vatNumber && !subject.taxCode
                ? 'Manca l’identificativo fiscale'
                : null;
    return { line, subject, issues, error };
}
/**
 * Sorts the rows of a file into what the import would do, without touching
 * anything: new subjects, subjects already in the registry (by tax identifier),
 * duplicates inside the file, rows sharing a VAT number with another row (a VAT
 * group: only one can be imported, the user chooses which), and errors.
 */
export function previewGestifyRows(rows, existing) {
    const knownVat = new Set(existing.vatNumbers);
    const knownTax = new Set(existing.taxCodes);
    const seenTax = new Map();
    const seenVat = new Map();
    const result = [];
    for (const row of rows) {
        const { vatNumber, taxCode } = row.subject;
        let action = 'new';
        let reason = null;
        if (row.error) {
            action = 'error';
            reason = row.error;
        }
        else if ((taxCode && knownTax.has(taxCode)) || (vatNumber && knownVat.has(vatNumber))) {
            action = 'existing';
            reason = 'Già in anagrafica: la riga viene saltata';
        }
        else if (taxCode && seenTax.has(taxCode)) {
            action = 'duplicate';
            reason = `Stesso codice fiscale della riga ${seenTax.get(taxCode)}`;
        }
        else if (vatNumber && seenVat.has(vatNumber)) {
            action = 'shared-vat';
            reason = `Stessa partita IVA della riga ${seenVat.get(vatNumber)}: scegli quale importare`;
        }
        if (action === 'new') {
            if (taxCode)
                seenTax.set(taxCode, row.line);
            if (vatNumber)
                seenVat.set(vatNumber, row.line);
        }
        result.push({ ...row, action, reason, selected: action === 'new' });
    }
    const summary = {
        new: 0,
        existing: 0,
        duplicate: 0,
        'shared-vat': 0,
        error: 0,
    };
    for (const row of result)
        summary[row.action]++;
    return { rows: result, summary };
}
/** Groups of the Gestify API → their names in the export; another code stays as it is and is reported. */
const API_GROUPS = {
    C: 'Cliente',
    F: 'Fornitore',
    CF: 'Cliente e fornitore',
    G: 'Generico',
};
/**
 * A record of `GET /api/anagrafica/v1/list` of Gestify, read by the browser
 * (decision of 05/10/2026), → a row as the export file has it: the preview and
 * the rules of the import stay the same for the file and for the API. Codes of
 * other tables come as `{ id, descrizione }`; the name is preferred, the code
 * is kept when the name is missing (the ISO code of the country, the sigla of
 * the province).
 */
export function gestifyApiRow(record) {
    const value = (raw) => {
        if (raw === null || raw === undefined)
            return null;
        if (typeof raw === 'object') {
            const item = raw;
            return text(item.descrizione) || text(item.id) || null;
        }
        return text(raw) || null;
    };
    const code = (raw) => raw !== null && typeof raw === 'object'
        ? text(raw.id) || null
        : value(raw);
    const fe = (record.fe ?? {});
    const group = text(record.gruppo_anagrafico);
    const person = [text(record.cognome), text(record.nome)].filter(Boolean).join(' ');
    return {
        [GESTIFY_COLUMNS.code]: value(record.codice_manuale) ?? value(record.id),
        [GESTIFY_COLUMNS.group]: API_GROUPS[group] ?? (group || null),
        [GESTIFY_COLUMNS.type]: value(record.tipologia),
        [GESTIFY_COLUMNS.name]: value(record.ragione_sociale) ?? (person || null),
        [GESTIFY_COLUMNS.address]: value(record.indirizzo),
        [GESTIFY_COLUMNS.streetNumber]: value(record.civico),
        [GESTIFY_COLUMNS.postalCode]: value(record.cap),
        [GESTIFY_COLUMNS.city]: value(record.comune) ?? value(record.localita),
        [GESTIFY_COLUMNS.province]: code(record.provincia),
        [GESTIFY_COLUMNS.country]: code(record.nazione),
        [GESTIFY_COLUMNS.vatNumber]: value(record.partita_iva),
        [GESTIFY_COLUMNS.taxCode]: value(record.codice_fiscale),
        [GESTIFY_COLUMNS.sdiCode]: value(fe.codice_destinatario),
        [GESTIFY_COLUMNS.pec]: value(fe.pec) ?? value(record.pec),
    };
}
//# sourceMappingURL=gestify-import.js.map